import type { JSONContent } from '@tiptap/core';
import {
  blockTypeSupportsSettings,
  getBlockTitleStrategy,
  getCourseBlockTypeLabel,
  type CourseBlock,
} from '@course-authoring/shared-types';

const COPY_SUFFIX = ' - copy';

function normalizeText(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function truncateLabel(value: string, maxLength = 80): string {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength - 3).trimEnd()}...`;
}

function extractPlainTextFromRichContent(content?: JSONContent): string {
  const fragments: string[] = [];

  const walk = (node?: JSONContent) => {
    if (!node || typeof node !== 'object') {
      return;
    }

    if (typeof node.text === 'string') {
      fragments.push(node.text);
    }

    if (Array.isArray(node.content)) {
      const appendSpacer = node.type === 'paragraph' || node.type === 'heading';
      const startLength = fragments.length;

      node.content.forEach((child) => walk(child));

      if (appendSpacer && fragments.length > startLength) {
        fragments.push('\n');
      }
    }
  };

  walk(content);

  return normalizeText(fragments.join(''));
}

function appendCopySuffixToStringTitle(title: string): string;
function appendCopySuffixToStringTitle(title?: string): string | undefined;
function appendCopySuffixToStringTitle(title?: string): string | undefined {
  if (typeof title !== 'string') {
    return title;
  }

  const trimmedTitle = normalizeText(title);
  if (!trimmedTitle) {
    return title;
  }

  return `${trimmedTitle}${COPY_SUFFIX}`;
}

function appendCopySuffixToRichContentTitle(content: JSONContent): JSONContent {
  if (!extractPlainTextFromRichContent(content)) {
    return content;
  }

  const clonedContent = structuredClone(content);

  const appendToLastTextNode = (node: JSONContent): boolean => {
    if (Array.isArray(node.content)) {
      for (let index = node.content.length - 1; index >= 0; index -= 1) {
        const child = node.content[index];
        if (child && appendToLastTextNode(child)) {
          return true;
        }
      }

      if (node.type === 'paragraph' || node.type === 'heading') {
        node.content = [
          ...(node.content ?? []),
          { type: 'text', text: COPY_SUFFIX },
        ];
        return true;
      }
    }

    if (node.type === 'text' && typeof node.text === 'string') {
      node.text = `${node.text}${COPY_SUFFIX}`;
      return true;
    }

    return false;
  };

  if (appendToLastTextNode(clonedContent)) {
    return clonedContent;
  }

  return clonedContent;
}

function getPlainBlockTitle(block: CourseBlock): string | undefined {
  if (!('title' in block)) {
    return undefined;
  }

  const ownTitle = typeof block.title === 'string' ? block.title : undefined;

  // NA-384: a block inheriting its title from the Question Bank stores
  // `title: undefined` (see useFieldOverrideState's buildRevert) so the next
  // read re-resolves the bank value — mirror that fallback here, the same
  // way the editor's own displayValue does, instead of reading `title` raw.
  if ('titleBankValue' in block && block.titleOverridden !== true) {
    const bankValue = block.titleBankValue;
    if (typeof bankValue === 'string') {
      return bankValue;
    }
  }

  return ownTitle;
}

function getRichBlockTitle(block: CourseBlock): JSONContent | undefined {
  if ('title' in block && block.title && typeof block.title === 'object') {
    return block.title as JSONContent;
  }

  return undefined;
}

export function blockSupportsSettings(block: CourseBlock): boolean {
  // NA-411: a gated Single Choice Answer's only setting (hide results) is
  // suppressed in BlockSettingsModal, so the gear would open an empty modal.
  if (block.type === 'single-choice-answer' && block.gated === true) {
    return false;
  }
  return blockTypeSupportsSettings(block.type);
}

export function getBlockTypeLabel(block: CourseBlock): string {
  return getCourseBlockTypeLabel(block);
}

export function getBlockDisplayTitle(block: CourseBlock): string {
  const typeLabel = getBlockTypeLabel(block);
  const titleStrategy = getBlockTitleStrategy(block.type);
  const candidateTitle =
    titleStrategy === 'plain'
      ? normalizeText(getPlainBlockTitle(block) ?? '')
      : titleStrategy === 'rich'
        ? extractPlainTextFromRichContent(getRichBlockTitle(block))
        : '';

  if (candidateTitle) {
    return truncateLabel(candidateTitle);
  }

  return `Untitled ${typeLabel}`;
}

export function appendCopySuffixToBlockTitle(block: CourseBlock): CourseBlock {
  const titleStrategy = getBlockTitleStrategy(block.type);

  if (titleStrategy === 'plain') {
    const existingTitle = getPlainBlockTitle(block);
    const title = appendCopySuffixToStringTitle(existingTitle);

    if (!('title' in block) || typeof block.title !== 'string' || title === block.title) {
      return block;
    }

    return { ...block, title } as CourseBlock;
  }

  if (titleStrategy === 'rich') {
    const existingTitle = getRichBlockTitle(block);
    if (!existingTitle || !extractPlainTextFromRichContent(existingTitle)) {
      return block;
    }

    return {
      ...block,
      title: appendCopySuffixToRichContentTitle(existingTitle),
    } as CourseBlock;
  }

  return block;
}
